import { MenuItemType } from '@/app/types'
import { scrollToElement } from '@/app/utils/layout'
import {
  filterMenuByRole,
  withKeyLevelUnsetBadge,
  withMetadataChangePendingBadge,
} from '@/app/utils/menu-visibility'
import { effectiveRoles, hasEffectiveRole, USER_ROLE_IDS } from '@/app/utils/user-role'
import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router, RouterLink } from '@angular/router'
import { Icon } from '@app/components/icon/icon'
import { LayoutService } from '@core/services/common'
import {
  KeyDomainService,
  MetadataChangeLogService,
  MetaDataDatabaseService,
  UserService,
} from '@core/services/api'
import { menuItems } from '@layouts/components/data'
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap'
import { catchError, filter, map, of } from 'rxjs'

@Component({
  selector: 'app-menu',
  imports: [NgbCollapse, RouterLink, CommonModule, Icon],
  template: `
    <ul class="side-nav">
      @for (item of menuItems(); track item.slug) {
        @if (item.isTitle) {
          <li class="side-nav-title mt-2">{{ item.label }}</li>
        }
        @for (child of item.isTitle ? (item.children || []) : (item.children || [item]); track child.slug) {
          @if (hasSubMenu(child)) {
            <ng-container *ngTemplateOutlet="menuItemWithChildren; context: { item: child, isTopLevel: true }"></ng-container>
          } @else {
            <ng-container *ngTemplateOutlet="menuItem; context: { item: child, isTopLevel: true }"></ng-container>
          }
        }
      }
    </ul>

    <ng-template #menuItemWithChildren let-item="item" let-isTopLevel="isTopLevel">
      <li class="side-nav-item" [class.active]="isChildActive(item)">
        <button class="side-nav-link" (click)="toggleCollapse(item)" [attr.aria-expanded]="!isCollapsed(item)" [class.active]="isChildActive(item)">
          @if (item.icon && isTopLevel) {
            <span class="menu-icon"><app-icon [icon]="item.icon"></app-icon></span>
          }
          @if (!isTopLevel) {
            <span class="sub-menu-bullet" aria-hidden="true">•</span>
          }
          <span class="menu-text">{{ item.label }}</span>
          @if (item.badge) {
            <span class="badge {{ item.badge.className }}">{{ item.badge.text }}</span>
          } @else {
            <div class="menu-arrow"></div>
          }
        </button>

        <div [ngbCollapse]="isCollapsed(item)">
          <ul class="sub-menu">
            @for (child of item.children; track child.slug) {
              @if (hasSubMenu(child)) {
                <ng-container *ngTemplateOutlet="menuItemWithChildren; context: { item: child, isTopLevel: false }"></ng-container>
              } @else {
                <ng-container *ngTemplateOutlet="menuItem; context: { item: child, isTopLevel: false }"></ng-container>
              }
            }
          </ul>
        </div>
      </li>
    </ng-template>

    <ng-template #menuItem let-item="item" let-isTopLevel="isTopLevel">
      <li class="side-nav-item" [class.active]="isActive(item)">
        <a [routerLink]="item.url" class="side-nav-link" [attr.data-active-link]="isActive(item)" [class.disabled]="item.isDisabled" [class.special-menu]="item.isSpecial">
          @if (item.icon && isTopLevel) {
            <span class="menu-icon">
              <app-icon [icon]="item.icon"></app-icon>
            </span>
          }
          @if (!isTopLevel) {
            <span class="sub-menu-bullet" aria-hidden="true">•</span>
          }
          <span class="menu-text">{{ item.label }}</span>
          @if (item.badge) {
            <span class="badge {{ item.badge.className }}" [attr.title]="item.badge.title ?? 'รอยืนยัน ' + item.badge.text">{{ item.badge.text }}</span>
          }
        </a>
      </li>
    </ng-template>
  `,
  styles: ``,
})
export class AppMenu implements OnInit {
  router = inject(Router)
  layout = inject(LayoutService)
  private userService = inject(UserService)
  private changeLogService = inject(MetadataChangeLogService)
  private keyDomainService = inject(KeyDomainService)
  private metaDataDatabaseService = inject(MetaDataDatabaseService)
  private destroyRef = inject(DestroyRef)

  @ViewChild('MenuItemWithChildren', { static: true })
  menuItemWithChildren!: TemplateRef<{ item: MenuItemType }>

  @ViewChild('MenuItem', { static: true })
  menuItem!: TemplateRef<{ item: MenuItemType }>

  private explorerChildren = toSignal(
    this.metaDataDatabaseService
      .getMetaDataDatabases({ page: 1, page_size: 100, status: 'ACTIVE', order_by: 'order_by:asc,database_source_alias:asc' })
      .pipe(
        map((result) =>
          result.data
            .filter((db) => !!db.database_source_alias)
            .map(
              (db): MenuItemType => ({
                slug: `pages:warehouse-data:${db.database_source_alias}`,
                label: db.database_source_alias!,
                url:
                  db.source_type === 'Pasutara'
                    ? '/data-explorer/pasutara'
                    : `/data-explorer/${db.database_source_alias}`,
              })
            )
        ),
        catchError(() => of([] as MenuItemType[]))
      ),
    { initialValue: [] as MenuItemType[] }
  )

  readonly menuItems = computed(() => {
    const user = this.userService.currentUser()
    const roles = effectiveRoles(user)
    const explorerChildren = this.explorerChildren()
    const items = menuItems.map((item) => {
      if (!item.isTitle) return item
      return {
        ...item,
        children: (item.children ?? []).map((child) => {
          if (child.slug !== 'pages:warehouse-data') return child
          return { ...child, children: explorerChildren }
        }),
      }
    })
    return withKeyLevelUnsetBadge(
      withMetadataChangePendingBadge(
        filterMenuByRole(items, roles),
        this.changeLogService.pendingTableCount(),
      ),
      this.keyDomainService.unsetCount(),
    )
  })

  openSubmenus: { [key: string]: boolean } = {}

  ngOnInit(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.initializeOpenStates(this.menuItems())
      setTimeout(() => this.scrollToActiveLink(), 50)
    })

    this.initializeOpenStates(this.menuItems())
    setTimeout(() => this.scrollToActiveLink(), 100)
    this.refreshPendingBadge()
    this.refreshKeyLevelBadge()
  }

  private refreshPendingBadge(): void {
    if (!hasEffectiveRole(this.userService.currentUser(), USER_ROLE_IDS.SYSTEM_ADMIN, USER_ROLE_IDS.DATA_OWNER)) return
    this.changeLogService
      .getSummary()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()
  }

  private refreshKeyLevelBadge(): void {
    if (!hasEffectiveRole(this.userService.currentUser(), USER_ROLE_IDS.SYSTEM_ADMIN, USER_ROLE_IDS.DATA_OWNER)) return
    this.keyDomainService
      .refreshUnsetCount()
      .pipe(
        catchError(() => of(0)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()
  }

  hasSubMenu(item: MenuItemType): boolean {
    return !!item.children && item.children?.length > 0
  }

  initializeOpenStates(items: MenuItemType[]) {
    for (const item of items) {
      if (this.hasSubMenu(item)) {
        this.openSubmenus[item.slug] = this.isChildActive(item)
        this.initializeOpenStates(item.children || [])
      }
    }
  }

  isCollapsed(item: MenuItemType): boolean {
    return !this.openSubmenus[item.slug]
  }

  toggleCollapse(item: MenuItemType): void {
    this.openSubmenus[item.slug] = !this.openSubmenus[item.slug]
  }

  private isActiveUrl(item: MenuItemType): boolean {
    if (!item.url) return false
    const current = this.router.url.split('?')[0]
    if (item.url === '/') {
      return current === '/' || current === ''
    }
    // Keep parent menu active on nested pages (e.g. /oauth-clients/:id/view)
    return current === item.url || current.startsWith(`${item.url}/`)
  }

  isChildActive(item: MenuItemType): boolean {
    if (this.isActiveUrl(item)) return true
    if (!item.children) return false
    return item.children.some((child: MenuItemType) => this.isChildActive(child))
  }

  isActive(item: MenuItemType): boolean {
    return this.isActiveUrl(item)
  }

  scrollToActiveLink(): void {
    const activeItem = document.querySelector('[data-active-link="true"]') as HTMLElement
    const scrollContainer = document.querySelector('#sidenav .simplebar-content-wrapper') as HTMLElement

    if (activeItem && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect()
      const itemRect = activeItem.getBoundingClientRect()

      const offset = itemRect.top - containerRect.top - window.innerHeight * 0.4

      scrollToElement(scrollContainer, scrollContainer.scrollTop + offset, 500)
    }
  }
}
